/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps, import/no-anonymous-default-export, no-useless-escape */
import React,{useState,useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import Layout from '../components/layout/Layout';
import {Input,Select} from '../components/common/FormField';
import api from '../services/api';
import { useMultiMaster } from '../hooks/useMaster';
import {getCurrencySymbol} from '../utils/locale';

const BED_TYPES=['GENERAL','SEMI_PRIVATE','PRIVATE','DELUXE','ICU','NICU','PICU'];
const WARD_TYPES=['GENERAL','SPECIAL','ICU','NICU','PICU','SURGICAL','MEDICAL','MATERNITY','PEDIATRIC'];

export default function WardBedSetupPage(){
  const navigate=useNavigate();
  const [loading,setLoading]=useState(true);
  const [section,setSection]=useState<'ward'|'bed'>('ward');
  const {masters:ward_masters}=useMultiMaster(['WARD_TYPE','BED_TYPE']);
  const MASTER_WARD_TYPES=ward_masters['WARD_TYPE']?.map((m:any)=>m.value)||WARD_TYPES;
  const MASTER_BED_TYPES=ward_masters['BED_TYPE']?.map((m:any)=>({code:m.code,label:m.value,rate:m.extraData?.daily_rate||0}))||[];

  const [wards,setWards]=useState<any[]>([]);
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState('');
  const [wardForm,setWardForm]=useState({name:'',wardCode:'',wardType:'GENERAL',floor:'',totalBeds:''});
  const [bedForm,setBedForm]=useState({bedNumber:'',wardId:'',bedType:'GENERAL',roomNumber:'',dailyRate:''});

  useEffect(()=>{api.getWards().then(r=>setWards(r.data||[])).catch(()=>{});},[]);

  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),4000);};

  const addWard=async(e:React.FormEvent)=>{
    e.preventDefault();setSaving(true);
    try{
      await api.createWard(wardForm);
      notify('✅ Ward added!');
      setWardForm({name:'',wardCode:'',wardType:'GENERAL',floor:'',totalBeds:''});
      api.getWards().then(r=>setWards(r.data||[])).catch(()=>{});
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  const addBed=async(e:React.FormEvent)=>{
    e.preventDefault();setSaving(true);
    try{
      await api.createBed(bedForm);
      notify('✅ Bed added!');
      setBedForm({bedNumber:'',wardId:bedForm.wardId,bedType:'GENERAL',roomNumber:'',dailyRate:''});
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    finally{setSaving(false);}
  };

  return(
    <Layout title="Ward & Bed Setup" actions={<button onClick={()=>navigate('/ipd')} className="btn-secondary text-sm">← Back to IPD</button>}>
      {msg&&<div className={`mb-4 p-3 rounded-lg text-sm border ${msg.startsWith('✅')?'bg-green-50 text-green-700 border-green-200':'bg-red-50 text-red-700 border-red-200'}`}>{msg}</div>}

      <div className="flex gap-1 mb-5 border-b pb-1">
        <button onClick={()=>setSection('ward')} className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-all ${section==='ward'?'bg-[#02357A] text-white':'text-slate-600 hover:bg-slate-100'}`}>🏢 Add Ward</button>
        <button onClick={()=>setSection('bed')} className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-all ${section==='bed'?'bg-[#02357A] text-white':'text-slate-600 hover:bg-slate-100'}`}>🛏️ Add Bed</button>
      </div>

      {section==='ward'&&(
        <form onSubmit={addWard} className="card p-6 space-y-4 max-w-2xl">
          <h3 className="font-semibold text-slate-700">New Ward</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Ward Name *</label><Input value={wardForm.name} onChange={e=>setWardForm({...wardForm,name:e.target.value})} required/></div>
            <div><label className="label">Ward Code</label><Input value={wardForm.wardCode} onChange={e=>setWardForm({...wardForm,wardCode:e.target.value})} placeholder="e.g. GEN-W1"/></div>
            <div><label className="label">Ward Type</label><Select value={wardForm.wardType} onChange={e=>setWardForm({...wardForm,wardType:e.target.value})}>{WARD_TYPES.map(t=><option key={t}>{t}</option>)}</Select></div>
            <div><label className="label">Floor</label><Input value={wardForm.floor} onChange={e=>setWardForm({...wardForm,floor:e.target.value})} placeholder="e.g. 2, Ground"/></div>
            <div><label className="label">Total Beds</label><Input type="number" value={wardForm.totalBeds} onChange={e=>setWardForm({...wardForm,totalBeds:e.target.value})}/></div>
          </div>
          <div className="flex gap-3 pt-3 border-t"><button type="submit" disabled={saving} className="btn-primary flex-1">{saving?'Adding...':'Add Ward'}</button></div>

          {wards.length>0&&<div className="pt-4 border-t">
            <p className="text-xs font-semibold text-slate-500 mb-2">Existing Wards ({wards.length})</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {wards.map((w:any)=><div key={w.id} className="bg-slate-50 rounded-lg p-2.5 text-sm"><p className="font-medium">{w.name}</p><p className="text-xs text-slate-400">{w.wardType} · {w.totalBeds||0} beds</p></div>)}
            </div>
          </div>}
        </form>
      )}

      {section==='bed'&&(
        <form onSubmit={addBed} className="card p-6 space-y-4 max-w-2xl">
          <h3 className="font-semibold text-slate-700">New Bed</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Bed Number *</label><Input value={bedForm.bedNumber} onChange={e=>setBedForm({...bedForm,bedNumber:e.target.value})} required/></div>
            <div><label className="label">Ward *</label><Select value={bedForm.wardId} onChange={e=>setBedForm({...bedForm,wardId:e.target.value})} required><option value="">Select Ward</option>{wards.map((w:any)=><option key={w.id} value={w.id}>{w.name}</option>)}</Select></div>
            <div><label className="label">Bed Type</label><Select value={bedForm.bedType} onChange={e=>setBedForm({...bedForm,bedType:e.target.value})}>{BED_TYPES.map(t=><option key={t}>{t}</option>)}</Select></div>
            <div><label className="label">Room Number</label><Input value={bedForm.roomNumber} onChange={e=>setBedForm({...bedForm,roomNumber:e.target.value})}/></div>
            <div><label className="label">Daily Rate ({getCurrencySymbol()})</label><Input type="number" value={bedForm.dailyRate} onChange={e=>setBedForm({...bedForm,dailyRate:e.target.value})}/></div>
          </div>
          <div className="flex gap-3 pt-3 border-t"><button type="submit" disabled={saving} className="btn-primary flex-1">{saving?'Adding...':'Add Bed'}</button></div>
          {wards.length===0&&<p className="text-xs text-amber-600">⚠️ Add a ward first before adding beds.</p>}
        </form>
      )}
    </Layout>
  );
}
